import { IExpiredRaSearchForm, IGenericOption, IRaSearchForm, IRaStatus } from "../models/models";
import {ColDef, SideBarDef, ValueFormatterParams, ValueGetterParams} from "ag-grid-community";
import dayjs from "dayjs";
import * as yup from "yup";

export const DMS_NAMING = [
    {
        code: "DMS_ML",
        description: "DMS Milan",
        label: "DMS Milan",
        regionCode: "3"
    },
    {
        code: "DMS_RMC",
        description: "DMS Ciampino",
        label: "DMS Roma Ciampino",
        regionCode: "1"
    },
    {
        code: "DMS_RMF",
        description: "DMS Fiumicino",
        label: "DMS Roma Fiumicino",
        regionCode: "1"
    },
    {
        code: "DMS_FR",
        description: "DMS Firenze",
        label: "DMS Firenze",
        regionCode: "2"
    }
];

export const REGION = [
    {
        code: "3",
        description: "Milano",
        label: "Milano",
    },
    {
        code: "2",
        description: "Firenze",
        label: "Firenze",
    },
    {
        code: "1",
        description: "Roma",
        label: "Roma",
    }
];

export const EXPIRED_RA_STATUS = {
    raStatus: {
        code: "EXPIRED",
        description: "Expired"
    }
}

export const RA_STATUS_OPTIONS: IRaStatus[] = [
    {
        code: "ONGOING",
        description: "Ongoing"
    },
    {
        code: "CLOSED",
        description: "Closed"
    },
    {
        code: "VOID",
        description: "Void"
    },
    // {
    //     code: "STOLEN",
    //     description: "Stolen"
    // },
    {
        code: "EXPIRED",
        description: "Expired"
    },
    {
        code: "DRAFT",
        description: "Draft"
    },
    // {
    //     code: "EMBEZZLED",
    //     description: "Embezzled"
    // },
    {
        code: "READY_PAID",
        description: "Ready-Paid",
    },
    {
        code: "READY_TO_SIGN",
        description: "Ready-To Sign",
    },
    {
        code: "READY_SIGNED",
        description: "Ready-Signed",
    },
    {
        code: "READY_TO_PAY",
        description: "Ready-To Pay",
    },
];

export const RA_STATUS_OPTIONS_NO_EXPIRED: IRaStatus[] = [
    {
        code: "ONGOING",
        description: "Ongoing"
    },
    {
        code: "CLOSED",
        description: "Closed"
    },
    {
        code: "VOID",
        description: "Void"
    },
    {
        code: "EXPIRED",
        description: "Expired"
    },
    {
        code: "DRAFT",
        description: "Draft"
    },
    {
        code: "READY_PAID",
        description: "Ready-Paid",
    },
    {
        code: "READY_TO_SIGN",
        description: "Ready-To Sign",
    },
    {
        code: "READY_SIGNED",
        description: "Ready-Signed",
    },
    {
        code: "READY_TO_PAY",
        description: "Ready-To Pay",
    },
];

export const DMS_OPTIONS: IGenericOption[] = [
    {
        code: "CPO",
        description: "CPO"
    },
    {
        code: "MPS",
        description: "MPS"
    }
];

export const RA_SEARCH_INITIAL_VALUE: IRaSearchForm = {
    idRentalAgreement: '',
    dmsIdentificationCode: null,
    pickUpLocation: null,
    pickUpDateFrom: null,
    pickUpDateTo: null,
    renewalsAndSubstitutions: '',
    plate: '',
    rfidCard: '',
    mainDriver: '',
    dropOffLocation: null,
    dropOffDateFrom: null,
    dropOffDateTo: null,
    reservationNumber: '',
    dossier: '',
    raStatus: null,
    cbId: '',
    acrissId: null,
    toBeReconciled: false,
    recoveryMode: false,
    tenants: null,
    dealers: null,
    keyless: '',
}

export const RA_SEARCH_FORM_VALIDATION_SCHEMA = yup.object({
    tenants: yup.array()
        .nullable()
        .test({
            name: "tenants",
            message: "common.error.required",
            test(value, context) {
                return !!value;
            }
        })
})

export const EXPIRED_RA_SEARCH_INITIAL_VALUE: IExpiredRaSearchForm = {
    region: null,
    dmsNaming: null
}

/** AG Grid Config */
export const EXPIRED_DEFAULT_COL_DEF: ColDef = {
    resizable: true,
    lockPinned: true,
    unSortIcon: true,
    suppressSizeToFit: true
};

export const EXPIRED_SIDE_BAR: SideBarDef = {
    toolPanels: [
        {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressColumnExpandAll: true,
            },
        },
    ],
    defaultToolPanel: '',
    position: 'right'
};

// General
export function getGeneralColumnDefs(t: any): ColDef[] {
    return [
        {
            field: 'idRentalAgreement',
            headerName: t('raSearch.table.headerName.idRentalAgreement'),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'dmsPickUp',
            headerName: t('raSearch.table.headerName.dmsIdCode'),
            pinned: 'left',
            valueFormatter: (params: ValueFormatterParams<any, IGenericOption>) => (params.value ? params.value.description : ''),
            sortable: true
        },
        {
            field: 'pickUpDate',
            headerName: t('raSearch.table.headerName.pickUpDate'),
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'renewalsAndSubstitutions',
            headerName: t('raSearch.table.headerName.renewalsAndSubstitutions'),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'dmsDropOff',
            headerName: t('raSearch.table.headerName.dmsDropOff'),
            valueFormatter: (params: ValueFormatterParams<any, IGenericOption>) => (params.value ? params.value.description : ''),
            sortable: true
        },
        {
            field: 'dropOffDate',
            headerName: t('raSearch.table.headerName.dropOffDate'),
            valueFormatter: (params: ValueFormatterParams<any, string>) => {
              let dropOffDate = '';
              if (params.value) {
                dropOffDate = dayjs(params.value).format('DD/MM/YYYY HH:mm');
              } else if (params.data.expectedDropOffDate) {
                dropOffDate = dayjs(params.data.expectedDropOffDate).format('DD/MM/YYYY HH:mm');
              }
              return dropOffDate;
            },
            sortable: true
        },
        {
            field: 'segment',
            headerName: t('raSearch.table.headerName.segment'),
            sortable: true
        },
        {
            field: 'cbId',
            headerName: t('raSearch.table.headerName.customerId'),
            sortable: true
        },
        {
            field: 'mainDriver',
            headerName: t('raSearch.table.headerName.mainDriver'),
            sortable: true
        },
        {
            field: 'firstBillingOwner',
            headerName: t('raSearch.table.headerName.firstBillingOwner'),
            sortable: true
        },
        {
            field: 'toBeReconciled',
            headerName: t('raSearch.table.headerName.toBeReconciled'),
            sortable: true,
            valueGetter: (params: ValueGetterParams<IRaSearchForm>) => params.data?.toBeReconciled ? t('raSearch.table.headerName.yes') : t('raSearch.table.headerName.no')
        },
        {
            field: 'recoveryMode',
            headerName: t('raSearch.table.headerName.recoveryMode'),
            sortable: true,
            valueGetter: (params: ValueGetterParams<IRaSearchForm>) => params.data?.recoveryMode ? t('raSearch.table.headerName.yes') : t('raSearch.table.headerName.no')
        },
        {
            field: 'plate',
            headerName: t('raSearch.table.headerName.plate'),
            sortable: true
        },
        {
            field: 'rfidCard',
            headerName: t('raSearch.table.headerName.rfidCard'),
            sortable: true
        },
        {
            field: 'reservationNumber',
            headerName: t('raSearch.table.headerName.reservationNumber'),
            sortable: true
        },
        {   // acris
            field: 'acrissDescription',
            headerName: t('raSearch.table.headerName.carGroupModel'),
            sortable: true
        },
        {
            field: 'acrissId',
            headerName: t('raSearch.table.headerName.effectiveCarGroup'),
            sortable: true
        },
        {
            field: 'dossierNumber',
            headerName: t('raSearch.table.headerName.dossier'),
            sortable: true
        },
        {
            field: 'raStatus.description',
            headerName: t('raSearch.table.headerName.raStatus'),
            sortable: true
        },
        {
            field: 'keyless',
            headerName: t('raSearch.table.headerName.keyless'),
            minWidth: 200,
            sortable: true,
            cellRenderer: (params: any) => {
                return t(params.data.keyless ? 'raSearch.table.headerName.yes' : 'raSearch.table.headerName.no')
            }
        }
    ];
}

// Expired
export function getExpiredColumnDefs(t: any): ColDef[] {
    return [
        {
            field: 'raNumber',
            headerName: t('raSearch.table.headerName.raNumber'),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'dmsPickUp',
            headerName: t('raSearch.table.headerName.dmsIdCode'),
            valueFormatter: (params: ValueFormatterParams<any, IGenericOption>) => (params.value ? params.value.description : ''),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'pickUpDate',
            headerName: t('raSearch.table.headerName.pickUpDate'),
            pinned: 'left',
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
            sortable: true
        },
        {
            field: 'renewalsAndSubstitutions',
            headerName: t('raSearch.table.headerName.renewalsAndSubstitutions'),
            pinned: 'left',
            sortable: true
        },
        {
            field: 'dmsDropOff',
            headerName: t('raSearch.table.headerName.dmsDropOff'),
            valueFormatter: (params: ValueFormatterParams<any, IGenericOption>) => (params.value ? params.value.description : ''),
            sortable: true
        },
        {
            field: 'dropOffDate',
            headerName: t('raSearch.table.headerName.dropOffDate'),
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
            sortable: true
        },
        {
            field: 'expectedDropOffDate',
            headerName: t('raSearch.table.headerName.expectedDropOffDate'),
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
            sortable: true
        },
        {   // TODO : rename with accris & add valueFormatter....
            field: 'carGroupModel',
            headerName: t('raSearch.table.headerName.carGroupModel'),
            sortable: true
        },
        {
            field: 'plate',
            headerName: t('raSearch.table.headerName.plate'),
            sortable: true
        },
        {
            field: 'billingCarGroup',
            headerName: t('raSearch.table.headerName.billingCarGroup'),
            sortable: true
        },
        {
            field: 'segment',
            headerName: t('raSearch.table.headerName.segment'),
            sortable: true
        },
        {
            field: 'firstBillingOwner',
            headerName: t('raSearch.table.headerName.firstBillingOwner'),
            sortable: true
        },
        {
            field: 'mainDriver',
            headerName: t('raSearch.table.headerName.mainDriver'),
            sortable: true
        },
        {
            field: 'dayOverdue',
            headerName: t('raSearch.table.headerName.dayOverdue'),
            sortable: true
        }
    ];
}

export const actionHistoryMOCK = [
    {
        id: '1',
        creationDate: '2024-05-01T00:00:00.000Z',
        name: "Roberto Pirone",
        operator: 'Stefano Giana',
        action: 'NEW',
        daysLate: '7',
        recoveryCompany: '',
        blackList: false
    },
    {
        id: '2',
        creationDate: '2024-05-05T00:00:00.000Z',
        name: "Roberto Pirone",
        operator: 'Stefano Giana',
        action: 'SMS1',
        daysLate: '7',
        recoveryCompany: '',
        blackList: false
    },
    {
        id: '2',
        creationDate: '2024-05-10T00:00:00.000Z',
        name: "Roberto Pirone",
        operator: 'Stefano Giana',
        action: 'Affidamento',
        daysLate: '7',
        recoveryCompany: 'Caledonia',
        blackList: true
    }
];